import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navigation from '@/components/Navigation';
import { Shield, Lock, Eye, Globe, Users, FileText } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Privacy Policy - AI Mastery Hub</title>
        <meta name="description" content="AI Mastery Hub Privacy Policy - Learn how we collect, use, and protect your personal information." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-12 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
        }}></div>

        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-block px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-full text-sm mb-6">
            <span className="text-purple-400">🔒 Your Privacy Matters</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6">
            <span className="block bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Privacy Policy
            </span>
          </h1>
          <p className="text-xl text-gray-400">
            Last Updated: November 23, 2025
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/20">

            {/* Introduction */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Shield className="w-8 h-8 text-purple-400" />
                <h2 className="text-3xl font-bold">Introduction</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                AI Mastery Hub ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <a href="https://ai-mastery-hub.com" className="text-purple-400 hover:text-purple-300">ai-mastery-hub.com</a> and use our services.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
              </p>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* Information We Collect */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Eye className="w-8 h-8 text-blue-400" />
                <h2 className="text-3xl font-bold">Information We Collect</h2>
              </div>

              <h3 className="text-xl font-semibold text-purple-400 mb-3">Personal Information</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                We collect information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                <li>Register for an account (name, email address, username, password)</li>
                <li>Make a purchase (billing information, payment card details via Stripe)</li>
                <li>Subscribe to our newsletter or marketing communications</li>
                <li>Submit a contact form or communicate with us</li>
                <li>Participate in surveys, contests, or promotions</li>
                <li>Post content or comments on our platform</li>
              </ul>

              <h3 className="text-xl font-semibold text-blue-400 mb-3">Automatically Collected Information</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                When you access our services, we automatically collect certain information, including:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                <li>Log data (IP address, browser type, operating system, access times)</li>
                <li>Device information (device type, unique device identifiers)</li>
                <li>Usage data (pages viewed, links clicked, time spent on pages, course progress)</li>
                <li>Cookies and tracking technologies (see Cookies section below)</li>
                <li>Location data (general geographic location based on IP address)</li>
              </ul>

              <h3 className="text-xl font-semibold text-cyan-400 mb-3">Information from Third Parties</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                We may receive information about you from third parties, including:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>Payment processors (Stripe) - transaction information</li>
                <li>Analytics providers (Google Analytics) - usage patterns and demographics</li>
                <li>Social media platforms (if you choose to connect your accounts)</li>
                <li>Marketing partners and affiliates</li>
              </ul>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* How We Use Your Information */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <FileText className="w-8 h-8 text-green-400" />
                <h2 className="text-3xl font-bold">How We Use Your Information</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li><strong className="text-white">Provide Services:</strong> Create and manage your account, process transactions, deliver course content, and provide customer support</li>
                <li><strong className="text-white">Improve Services:</strong> Analyze usage patterns, conduct research, develop new features, and enhance user experience</li>
                <li><strong className="text-white">Communications:</strong> Send transactional emails, course updates, newsletters, promotional materials, and respond to inquiries</li>
                <li><strong className="text-white">Security:</strong> Protect against fraud, unauthorized access, and other illegal activities</li>
                <li><strong className="text-white">Legal Compliance:</strong> Comply with legal obligations, resolve disputes, and enforce our agreements</li>
                <li><strong className="text-white">Marketing:</strong> Send you promotional communications about new courses, features, and special offers (you can opt out anytime)</li>
                <li><strong className="text-white">Personalization:</strong> Customize your experience and recommend relevant courses based on your interests</li>
                <li><strong className="text-white">Analytics:</strong> Monitor and analyze trends, usage, and activities in connection with our services</li>
              </ul>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* Third-Party Service Providers */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Users className="w-8 h-8 text-yellow-400" />
                <h2 className="text-3xl font-bold">Third-Party Service Providers</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                We may share your information with third-party service providers who perform services on our behalf:
              </p>

              <div className="space-y-4">
                <div className="bg-black/30 rounded-xl p-4 border border-white/10">
                  <h4 className="font-semibold text-purple-400 mb-2">Payment Processing</h4>
                  <p className="text-gray-300 text-sm">
                    <strong>Stripe:</strong> We use Stripe to process payments. Your payment information is transmitted directly to Stripe and is not stored on our servers. Stripe's privacy policy: <a href="https://stripe.com/privacy" className="text-purple-400 hover:text-purple-300">stripe.com/privacy</a>
                  </p>
                </div>

                <div className="bg-black/30 rounded-xl p-4 border border-white/10">
                  <h4 className="font-semibold text-blue-400 mb-2">Cloud Infrastructure</h4>
                  <p className="text-gray-300 text-sm">
                    <strong>Amazon Web Services (AWS):</strong> We use AWS for hosting, storage (S3), and email delivery (SES). AWS privacy policy: <a href="https://aws.amazon.com/privacy" className="text-blue-400 hover:text-blue-300">aws.amazon.com/privacy</a>
                  </p>
                </div>

                <div className="bg-black/30 rounded-xl p-4 border border-white/10">
                  <h4 className="font-semibold text-green-400 mb-2">Analytics</h4>
                  <p className="text-gray-300 text-sm">
                    <strong>Google Analytics:</strong> We use Google Analytics to understand how users interact with our site. Google Analytics privacy policy: <a href="https://policies.google.com/privacy" className="text-green-400 hover:text-green-300">policies.google.com/privacy</a>
                  </p>
                </div>

                <div className="bg-black/30 rounded-xl p-4 border border-white/10">
                  <h4 className="font-semibold text-cyan-400 mb-2">Content Delivery</h4>
                  <p className="text-gray-300 text-sm">
                    <strong>MongoDB:</strong> We use MongoDB for database services. MongoDB privacy policy: <a href="https://www.mongodb.com/legal/privacy-policy" className="text-cyan-400 hover:text-cyan-300">mongodb.com/legal/privacy-policy</a>
                  </p>
                </div>
              </div>

              <p className="text-gray-300 leading-relaxed mt-4 text-sm">
                These third parties have access to your information only to perform specific tasks on our behalf and are obligated not to disclose or use it for any other purpose.
              </p>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* Cookies and Tracking */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Lock className="w-8 h-8 text-red-400" />
                <h2 className="text-3xl font-bold">Cookies and Tracking Technologies</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                We use cookies, web beacons, and similar tracking technologies to collect and store information about your interactions with our services.
              </p>

              <h3 className="text-xl font-semibold text-purple-400 mb-3">Types of Cookies We Use:</h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4 ml-4">
                <li><strong className="text-white">Essential Cookies:</strong> Required for the website to function properly (authentication, security)</li>
                <li><strong className="text-white">Functional Cookies:</strong> Remember your preferences and settings</li>
                <li><strong className="text-white">Analytics Cookies:</strong> Help us understand how visitors use our site (Google Analytics)</li>
                <li><strong className="text-white">Marketing Cookies:</strong> Track your browsing habits to deliver relevant advertisements</li>
              </ul>

              <h3 className="text-xl font-semibold text-blue-400 mb-3">Managing Cookies:</h3>
              <p className="text-gray-300 leading-relaxed">
                Most web browsers allow you to control cookies through their settings. However, disabling certain cookies may affect your ability to use some features of our services. To opt out of Google Analytics, visit: <a href="https://tools.google.com/dlpage/gaoptout" className="text-blue-400 hover:text-blue-300">tools.google.com/dlpage/gaoptout</a>
              </p>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* Data Security */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Shield className="w-8 h-8 text-purple-400" />
                <h2 className="text-3xl font-bold">Data Security</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4 ml-4">
                <li>Encryption of data in transit (HTTPS/TLS) and at rest</li>
                <li>Secure authentication and access controls</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Employee training on data protection and confidentiality</li>
                <li>Restricted access to personal information on a need-to-know basis</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee absolute security.
              </p>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* Your Privacy Rights */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Users className="w-8 h-8 text-green-400" />
                <h2 className="text-3xl font-bold">Your Privacy Rights</h2>
              </div>

              <p className="text-gray-300 leading-relaxed mb-4">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>

              <h3 className="text-xl font-semibold text-purple-400 mb-3">General Rights:</h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                <li><strong className="text-white">Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong className="text-white">Rectification:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong className="text-white">Erasure:</strong> Request deletion of your personal information ("right to be forgotten")</li>
                <li><strong className="text-white">Restriction:</strong> Request limitation on how we process your information</li>
                <li><strong className="text-white">Portability:</strong> Request a copy of your data in a machine-readable format</li>
                <li><strong className="text-white">Objection:</strong> Object to our processing of your personal information</li>
                <li><strong className="text-white">Withdraw Consent:</strong> Withdraw previously given consent at any time</li>
              </ul>

              <h3 className="text-xl font-semibold text-blue-400 mb-3">GDPR Rights (EU/EEA Residents):</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                If you are located in the European Union or European Economic Area, you have additional rights under the General Data Protection Regulation (GDPR), including the right to lodge a complaint with a supervisory authority.
              </p>

              <h3 className="text-xl font-semibold text-cyan-400 mb-3">CCPA Rights (California Residents):</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                California residents have the right to:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4 ml-4">
                <li>Know what personal information is collected, used, shared, or sold</li>
                <li>Delete personal information held by businesses</li>
                <li>Opt-out of the sale of personal information (we do not sell your information)</li>
                <li>Non-discrimination for exercising CCPA rights</li>
              </ul>

              <div className="bg-purple-600/10 border border-purple-500/30 rounded-xl p-6 mt-6">
                <h4 className="font-semibold text-purple-400 mb-2">How to Exercise Your Rights</h4>
                <p className="text-gray-300 text-sm">
                  To exercise any of these rights, please contact us at <a href="mailto:support@ai-mastery-hub.com" className="text-purple-400 hover:text-purple-300">support@ai-mastery-hub.com</a> or use our contact form. We will respond to your request within 30 days.
                </p>
              </div>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* Data Retention */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <FileText className="w-8 h-8 text-yellow-400" />
                <h2 className="text-3xl font-bold">Data Retention</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
              </p>
              <p className="text-gray-300 leading-relaxed">
                When we no longer need your information, we will securely delete or anonymize it. Account information is retained for the duration of your account plus 7 years for financial and legal compliance purposes.
              </p>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* Children's Privacy */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Users className="w-8 h-8 text-red-400" />
                <h2 className="text-3xl font-bold">Children's Privacy</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                Our services are not intended for children under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately at <a href="mailto:support@ai-mastery-hub.com" className="text-purple-400 hover:text-purple-300">support@ai-mastery-hub.com</a>.
              </p>
              <p className="text-gray-300 leading-relaxed">
                If we discover that we have collected personal information from a child under 18, we will delete that information as quickly as possible.
              </p>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* International Data Transfers */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Globe className="w-8 h-8 text-blue-400" />
                <h2 className="text-3xl font-bold">International Data Transfers</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                Your information may be transferred to, stored, and processed in countries other than your country of residence, including the United States, where our servers and third-party service providers are located.
              </p>
              <p className="text-gray-300 leading-relaxed">
                When we transfer your information internationally, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses approved by the European Commission, to protect your information in accordance with this Privacy Policy and applicable data protection laws.
              </p>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* Do Not Track */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Eye className="w-8 h-8 text-cyan-400" />
                <h2 className="text-3xl font-bold">Do Not Track Signals</h2>
              </div>
              <p className="text-gray-300 leading-relaxed">
                Some web browsers have a "Do Not Track" feature that signals to websites you visit that you do not want to have your online activity tracked. Our website does not currently respond to Do Not Track signals or similar mechanisms.
              </p>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* Changes to Privacy Policy */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <FileText className="w-8 h-8 text-purple-400" />
                <h2 className="text-3xl font-bold">Changes to This Privacy Policy</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4 ml-4">
                <li>Posting the updated Privacy Policy on this page with a new "Last Updated" date</li>
                <li>Sending you an email notification (if you have an account with us)</li>
                <li>Displaying a prominent notice on our website</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                Your continued use of our services after any changes indicates your acceptance of the updated Privacy Policy.
              </p>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* Contact Us */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Users className="w-8 h-8 text-green-400" />
                <h2 className="text-3xl font-bold">Contact Us</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-6">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>

              <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-xl p-6">
                <h4 className="font-semibold text-white mb-4">AI Mastery Hub</h4>
                <div className="space-y-2 text-gray-300">
                  <p><strong className="text-white">Email:</strong> <a href="mailto:support@ai-mastery-hub.com" className="text-purple-400 hover:text-purple-300">support@ai-mastery-hub.com</a></p>
                  <p><strong className="text-white">Email (Alternative):</strong> <a href="mailto:aicademy-hub@proton.me" className="text-purple-400 hover:text-purple-300">aicademy-hub@proton.me</a></p>
                  <p><strong className="text-white">Website:</strong> <a href="https://ai-mastery-hub.com" className="text-purple-400 hover:text-purple-300">ai-mastery-hub.com</a></p>
                  <p><strong className="text-white">Contact Form:</strong> <a href="/contact" className="text-purple-400 hover:text-purple-300">ai-mastery-hub.com/contact</a></p>
                </div>
              </div>
            </div>

            {/* Acknowledgment */}
            <div className="bg-blue-600/10 border border-blue-500/30 rounded-xl p-6">
              <p className="text-gray-300 text-sm leading-relaxed">
                <strong className="text-white">Acknowledgment:</strong> By using AI Mastery Hub, you acknowledge that you have read and understood this Privacy Policy and agree to the collection, use, and disclosure of your information as described herein.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-4">
                AI Mastery Hub
              </h3>
              <p className="text-gray-400 text-sm">
                Empowering learners worldwide with cutting-edge digital education.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Courses</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/courses" className="hover:text-white transition">All Courses</a></li>
                <li><a href="/#" className="hover:text-white transition">AI & Machine Learning</a></li>
                <li><a href="/#" className="hover:text-white transition">Prompt Engineering</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/#" className="hover:text-white transition">About Us</a></li>
                <li><a href="/contact" className="hover:text-white transition">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/privacy-policy" className="hover:text-white transition">Privacy Policy</a></li>
                <li><a href="/terms-of-service" className="hover:text-white transition">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-400">© 2025 AI Mastery Hub. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white transition">Twitter</a>
              <a href="#" className="text-gray-400 hover:text-white transition">LinkedIn</a>
              <a href="#" className="text-gray-400 hover:text-white transition">YouTube</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
